import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { ConnectWalletButton } from '../auth/ConnectWalletButton';
import { LineOfficialLink } from './LineOfficialLink';
import {
  ChevronDown,
  navItems,
  isNavActive,
  isNavGroupActive,
} from './navItems';

function MobileNavGroup({ item, pathname, onClose }) {
  const groupActive = isNavGroupActive(pathname, item.children);
  const [open, setOpen] = useState(groupActive);
  const Icon = item.icon;

  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
          groupActive
            ? 'bg-red-50 text-brand-red'
            : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
        }`}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <ul className="mt-1 space-y-1 border-l-2 border-red-100 ml-4 pl-2">
          {item.children.map(({ href, label, icon: ChildIcon, badge }) => {
            const active = isNavActive(pathname, href);
            return (
              <li key={href}>
                <Link
                  to={href}
                  onClick={onClose}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-brand-red text-white'
                      : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
                  }`}
                >
                  <ChildIcon className="h-5 w-5 shrink-0" />
                  <span className="flex-1">{label}</span>
                  {badge ? (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active
                          ? 'bg-brand-gold text-red-950'
                          : 'bg-brand-gold/20 text-brand-gold'
                      }`}
                    >
                      {badge}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

export function MobileNavDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    onClose();
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="ปิดเมนู"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <aside className="absolute right-0 top-0 flex h-full w-[min(320px,85vw)] flex-col border-l border-red-100 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-red-50 px-4 py-4">
          <p className="text-sm font-bold text-brand-red">เมนู</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="ปิดเมนู"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-red-50 hover:text-brand-red"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <MobileNavGroup
                  key={item.id}
                  item={item}
                  pathname={location.pathname}
                  onClose={onClose}
                />
              ) : (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    onClick={onClose}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                      isNavActive(location.pathname, item.href)
                        ? 'bg-brand-red text-white'
                        : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
                    }`}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </nav>

        <div className="border-t border-red-50 px-4 py-4 space-y-3">
          <LineOfficialLink variant="drawer" onNavigate={onClose} />
          <ConnectWalletButton variant="drawer" onNavigate={onClose} />
        </div>
      </aside>
    </div>
  );
}
